import { useState, useRef, useCallback } from "react";
import { useVoice } from "@/contexts/VoiceContext";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export function MicButton({ onTranscript, disabled, size = "md", className }) {
  const { transcribeAudio } = useVoice();
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [showHint, setShowHint] = useState(() => typeof window !== "undefined" && localStorage.getItem("omni_mic_hint_seen") !== "true");
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const timerRef = useRef(null);

  const dismissHint = useCallback(() => {
    localStorage.setItem("omni_mic_hint_seen", "true");
    setShowHint(false);
  }, []);

  const sizes = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-14 h-14",
  };
  const iconSizes = {
    sm: "w-3.5 h-3.5",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  const startRecording = useCallback(async () => {
    dismissHint();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(timerRef.current);
        setDuration(0);

        if (audioChunks.current.length === 0) return;

        const blob = new Blob(audioChunks.current, { type: mimeType });
        setTranscribing(true);
        const text = await transcribeAudio(blob, mimeType);
        setTranscribing(false);

        if (text) {
          onTranscript(text);
        } else {
          toast.info("Couldn't catch that. Try speaking louder.");
        }
      };

      recorder.start(250);
      mediaRecorder.current = recorder;
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Microphone access is blocked", {
          description: "Click the lock/camera icon in your browser's address bar, allow Microphone, then try again.",
          duration: 8000,
        });
      } else {
        toast.error("Could not access microphone", { description: "Check that a microphone is connected and try again." });
      }
    }
  }, [onTranscript, transcribeAudio, dismissHint]);

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      mediaRecorder.current.stop();
    }
    setRecording(false);
  }, []);

  const formatDuration = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (transcribing) {
    return (
      <div
        data-testid="mic-transcribing"
        className={cn(sizes[size], "rounded-full bg-primary/10 flex items-center justify-center", className)}
      >
        <Loader2 className={cn(iconSizes[size], "text-primary animate-spin")} />
      </div>
    );
  }

  if (recording) {
    return (
      <button
        data-testid="mic-stop-recording"
        onClick={stopRecording}
        className={cn(
          sizes[size],
          "relative rounded-full bg-destructive text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform",
          className
        )}
      >
        <div className="absolute inset-0 rounded-full bg-destructive/30 animate-ping" />
        <Square className={cn(iconSizes[size], "relative z-10")} fill="currentColor" />
        {size !== "sm" && (
          <span className="absolute -top-2 -right-1 text-[10px] bg-card text-foreground px-1.5 py-0.5 rounded-full border border-border/50 font-mono">
            {formatDuration(duration)}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="relative inline-block">
      {showHint && !disabled && (
        <>
          <span className={cn(sizes[size], "absolute inset-0 rounded-full bg-primary/25 animate-ping pointer-events-none")} />
          <span
            data-testid="mic-hint-tooltip"
            className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium bg-foreground text-background px-2 py-1 rounded-lg shadow-md pointer-events-none animate-in fade-in duration-300"
          >
            Tap to speak
          </span>
        </>
      )}
      <button
        data-testid="mic-start-recording"
        onClick={startRecording}
        disabled={disabled}
        className={cn(
          sizes[size],
          "relative rounded-full flex items-center justify-center transition-all hover:scale-105",
          disabled
            ? "bg-muted text-muted-foreground cursor-not-allowed"
            : "bg-primary/10 text-primary hover:bg-primary/20",
          className
        )}
        title="Voice input — tap to speak"
      >
        <Mic className={iconSizes[size]} strokeWidth={1.5} />
      </button>
    </div>
  );
}
